import { ReadonlyVec2, vec2 } from 'gl-matrix';
import type { Grid } from '~/engine/data/common/Grid';
import type { ContourSequence } from '~/engine/data/structure/ContourSequence';
import type { ContourSlice } from '~/engine/data/structure/ContourSlice';
import { pastImageOf } from '~/engine/data/structure/ContourTexture';
import { isPointInsidePolygon } from './polygonWinding';

const EVERY = [-Infinity, Infinity] as const;

/**
 * CPU mirror of the classification the stencil pass of {@link SdfGenerator}
 * performs: a texel is inside a slice's contours when its centre lies inside
 * an odd number of them (libtess WINDING_ODD), and the SDF built from a
 * slice has content exactly when some texel is inside. Answering this from
 * the contours lets a structure report whether it is empty before it is
 * drawn — or without ever being drawn — with the result the texture gives
 * afterwards, up to the texture's byte quantisation along the contour line.
 */

/**
 * Yields the integers of [first, last] starting from the middle and
 * alternating outward, so a search over a bounding box meets its filled
 * interior before its sparse edges.
 */
function* outward(first: number, last: number): Generator<number> {
    if (first > last) {
        return;
    }
    const middle = Math.floor((first + last) / 2);
    yield middle;
    for (
        let step = 1;
        middle + step <= last || middle - step >= first;
        step++
    ) {
        if (middle + step <= last) {
            yield middle + step;
        }
        if (middle - step >= first) {
            yield middle - step;
        }
    }
}

/** Odd-even rule across polygons: inside an odd number of them. */
function insideOddEven(
    point: ReadonlyVec2,
    polygons: ReadonlyArray<ReadonlyVec2[]>
): boolean {
    let inside = false;
    for (const polygon of polygons) {
        if (isPointInsidePolygon(point, polygon)) {
            inside = !inside;
        }
    }
    return inside;
}

/**
 * Whether any texel centre lies inside an odd number of `polygons`.
 * Only the texels among `columns` and `rows` count, every one by default.
 *
 * `polygons` are in the voxel space of the grid the SDF is rasterised on,
 * where texel centres sit at half-integer coordinates. Polygons with fewer
 * than three vertices enclose nothing and are ignored, as the tessellation
 * ignores them. A polygon's size is irrelevant: a sub-voxel polygon that
 * encloses a texel centre covers that texel, and one that lies between
 * centres — or has no area — covers none.
 */
export function polygonsCoverTexelCentre(
    polygons: ReadonlyArray<ReadonlyVec2[]>,
    columns = EVERY,
    rows = EVERY
): boolean {
    const closed = polygons.filter((polygon) => polygon.length >= 3);
    if (closed.length === 0) {
        return false;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const polygon of closed) {
        for (const vertex of polygon) {
            minX = Math.min(minX, vertex[0]);
            minY = Math.min(minY, vertex[1]);
            maxX = Math.max(maxX, vertex[0]);
            maxY = Math.max(maxY, vertex[1]);
        }
    }
    // The texel centres k + 0.5 that fall inside the bounding box.
    const firstColumn = Math.max(columns[0], Math.ceil(minX - 0.5));
    const lastColumn = Math.min(columns[1], Math.floor(maxX - 0.5));
    const firstRow = Math.max(rows[0], Math.ceil(minY - 0.5));
    const lastRow = Math.min(rows[1], Math.floor(maxY - 0.5));
    const centre = vec2.create();
    for (const row of outward(firstRow, lastRow)) {
        for (const column of outward(firstColumn, lastColumn)) {
            vec2.set(centre, column + 0.5, row + 0.5);
            if (insideOddEven(centre, closed)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Whether rasterising `sequence` into an SDF on `imageGrid` would mark any
 * texel inside — the emptiness the texture reports, answered without
 * building it. The texture is snapped to the image voxel boundaries at the
 * image resolution, so its texel centres are image voxel centres (extended
 * past the image to wherever the contours are) and the test runs on the
 * image lattice.
 */
export function contoursCoverTexelCentre(
    imageGrid: Grid,
    sequence: Pick<ContourSequence, 'contourSlices'>
): boolean {
    for (const slice of sequence.contourSlices) {
        if (polygonsCoverTexelCentre(inImageVoxels(imageGrid, slice))) {
            return true;
        }
    }
    return false;
}

/** The contours of `slice` as polygons in the voxel space of `imageGrid`. */
function inImageVoxels(imageGrid: Grid, slice: ContourSlice): vec2[][] {
    return slice.contours.map((contour) =>
        contour.vertices.map((vertex) => {
            const voxel = imageGrid.toVoxelSpace(vertex);
            return vec2.fromValues(voxel[0], voxel[1]);
        })
    );
}

/**
 * Where rasterising `sequence` would put content past the outermost voxel
 * centres of `imageGrid`, each contour slice on the image slice the rasteriser
 * rounds it to from the first slice of `textureGrid`, the grid it is drawn on.
 */
export function contoursPastImage(
    imageGrid: Grid,
    sequence: Pick<ContourSequence, 'contourSlices'>,
    textureGrid: Grid = imageGrid
) {
    const [sizeX, sizeY, sizeZ] = imageGrid.getSize();
    const bottom = textureGrid.getSliceCenterNormalPosition(0);
    // A whole number of slices, as the texture lies on the image lattice.
    const offset = Math.round(
        (bottom - imageGrid.getSliceCenterNormalPosition(0)) / imageGrid.resZ
    );
    return pastImageOf(
        sequence.contourSlices.map((slice) => {
            const polygons = inImageVoxels(imageGrid, slice);
            const covers = (columns = EVERY, rows = EVERY) =>
                polygonsCoverTexelCentre(polygons, columns, rows);
            const z =
                offset +
                Math.round((slice.normalPosition - bottom) / textureGrid.resZ);
            const inside = z >= 0 && z < sizeZ;
            const past = [
                inside && covers([-Infinity, -1]),
                inside && covers([sizeX, Infinity]),
                inside && covers(EVERY, [-Infinity, -1]),
                inside && covers(EVERY, [sizeY, Infinity]),
                z < 0 && covers(),
                z >= sizeZ && covers(),
            ];
            return [z, past] as const;
        })
    );
}
